
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  CardDescription
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronLeft, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import PDFCertificatePreview from '@/components/student-certification/certificate-templates/PDFCertificatePreview';
import { CertificateTemplate } from '@/types/certificateTemplate';
import { certificateTemplateService } from '@/services/certification/templates';

const EditTemplatePreviewPage: React.FC = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const [template, setTemplate] = useState<CertificateTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [previewData] = useState<Record<string, string>>({
    studentName: 'John Doe',
    courseName: 'Introduction to Programming',
    programTitle: 'Computer Science',
    completionDate: 'June 15, 2025',
    enrollmentDate: 'January 10, 2025',
    grade: 'A',
    instructorName: 'Dr. Jane Smith',
    teacherName: 'Prof. Robert Johnson',
    trainingTitle: 'Advanced Teaching Techniques',
    verificationCode: 'CERT-123456'
  });

  useEffect(() => {
    if (!templateId) {
      toast.error('Template ID is missing');
      navigate('/certificate-templates');
      return;
    }
    
    // Load template from session storage (if available from previous steps)
    const storedTemplate = sessionStorage.getItem('editTemplate');
    if (storedTemplate) {
      try {
        const parsedTemplate = JSON.parse(storedTemplate);
        setTemplate(parsedTemplate);
      } catch (error) {
        console.error('Error parsing stored template:', error);
      } finally {
        setLoading(false);
      }
    } else {
      toast.error('No template data found. Please start from the beginning.');
      navigate(`/certificate-templates/edit/${templateId}/basic-info`);
    }
  }, [templateId, navigate]);

  const handlePrevious = () => {
    if (templateId) {
      navigate(`/certificate-templates/edit/${templateId}/mapping`);
    }
  };

  const handleSaveTemplate = () => {
    if (!templateId || !template) {
      toast.error('Template data is missing');
      return;
    }
    
    try {
      // Update the template
      const updatedTemplate = certificateTemplateService.updateCertificateTemplate(
        templateId, 
        {
          name: template.name,
          description: template.description,
          type: template.type,
          backgroundColor: template.backgroundColor,
          backgroundImage: template.backgroundImage,
          isDefault: template.isDefault,
          elements: template.elements,
          metadata: template.metadata
        }
      );
      
      if (updatedTemplate) {
        toast.success('Certificate template updated successfully');
        
        // Clear session storage
        sessionStorage.removeItem('editTemplate');
        
        // Navigate back to templates list
        navigate('/certificate-templates');
      } else {
        toast.error('Failed to update certificate template');
      }
    } catch (error) {
      console.error('Error updating certificate template:', error);
      toast.error('An error occurred while updating the template');
    }
  };

  const handleCancel = () => {
    navigate('/certificate-templates');
  };

  if (loading) {
    return (
      <div className="container mx-auto py-8 flex items-center justify-center h-[50vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8">
      <Helmet>
        <title>Edit Certificate Template - Preview | NALC</title>
      </Helmet>
      
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Edit Certificate Template</h1>
        <p className="text-muted-foreground mt-2">
          Step 4: Preview and save your certificate template
        </p>
      </div>
      
      <div className="flex justify-center mb-6">
        <div className="flex items-center">
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 rounded-full bg-muted text-muted-foreground flex items-center justify-center">
              ✓
            </div>
            <span className="text-sm mt-1">Basic Info</span>
          </div>
          <div className="w-12 h-1 bg-primary mx-1"></div>
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 rounded-full bg-muted text-muted-foreground flex items-center justify-center">
              ✓
            </div>
            <span className="text-sm mt-1">PDF Upload</span>
          </div>
          <div className="w-12 h-1 bg-primary mx-1"></div>
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 rounded-full bg-muted text-muted-foreground flex items-center justify-center">
              ✓
            </div>
            <span className="text-sm mt-1">Field Mapping</span>
          </div>
          <div className="w-12 h-1 bg-primary mx-1"></div>
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
              4
            </div>
            <span className="text-sm mt-1 text-primary font-medium">Preview</span>
          </div>
        </div>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle>Certificate Preview</CardTitle>
          <CardDescription>
            Preview how the certificate will look with sample data and save your template
          </CardDescription>
        </CardHeader>
        <CardContent>
          {template && template.metadata?.isPdf && template.metadata?.pdfUrl && template.elements?.length > 0 ? (
            <PDFCertificatePreview
              pdfUrl={template.metadata.pdfUrl}
              elements={template.elements}
              certificateData={previewData}
            />
          ) : template && template.metadata?.isPdf && template.metadata?.pdfUrl ? (
            <div>
              <p className="text-amber-500 mb-4">No field mappings defined. The PDF will be used as is.</p>
              <div className="border rounded h-[500px]">
                <iframe
                  src={`${template.metadata.pdfUrl}#toolbar=0`}
                  className="w-full h-full"
                  title="PDF Certificate Template"
                />
              </div>
            </div>
          ) : template ? (
            <div>
              <p className="text-amber-500 mb-4">No PDF template uploaded. A standard certificate will be created.</p>
              <div className="border rounded h-[500px] flex items-center justify-center"
                style={{
                  backgroundColor: template.backgroundColor || '#ffffff',
                  backgroundImage: template.backgroundImage ? `url(${template.backgroundImage})` : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              >
                <div className="bg-white/90 p-8 rounded shadow-lg text-center max-w-md">
                  <h2 className="text-2xl font-bold mb-4">{template.name || 'Certificate Template'}</h2>
                  <p className="mb-2">{template.description || 'This is a preview of the certificate template.'}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No template data available for preview</p>
            </div>
          )}
          
          <div className="flex justify-between mt-8 pt-4 border-t">
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button type="button" onClick={handlePrevious}>
                <ChevronLeft className="mr-2 h-4 w-4" /> Previous
              </Button>
            </div>
            <Button onClick={handleSaveTemplate}>
              <Save className="mr-2 h-4 w-4" /> Save Template
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EditTemplatePreviewPage;
